import {
  Box,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@material-ui/core'
import { useUser } from 'components/providers/UserProvider'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PricingJobType, PricingPlan, PricingResult } from 'shared'
import { SetState } from 'types'

interface PricingBotActivityPresenterProps {
  updates: PricingResult[]
  onViewUpdate: (updateId: string) => void
  activePlan?: PricingPlan
  selectedPlanId: string
  setSelectedPlanId: SetState<string>
  isTestRunning: boolean
  setIsTestRunning: SetState<string>
  isManualRunning: boolean
  setIsManualRunning: SetState<string>
}

enum PriceUpdateTab {
  Nightly = 0,
  Manual,
  Test,
}

export const PriceUpdatesTable = ({
  updates,
  onViewUpdate,
}: {
  updates: PricingResult[]
  onViewUpdate: (updateId: string) => void
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Table style={{ maxWidth: '30rem' }}>
      <TableHead>
        <TableRow>
          <TableCell>
            <b>{t('common.date')}</b>
          </TableCell>
          <TableCell>
            <b>{t('priceUpdates.programUsed')}</b>
          </TableCell>
          <TableCell align="center">
            <b>{t('priceUpdates.viewResults')}</b>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {updates.map((update, index) => (
          <TableRow key={update._id}>
            <TableCell>
              <span>{moment(update.createdAt).format('DD.MM.YYYY HH:mm')}</span>
            </TableCell>
            <TableCell>
              <span>{update.details?.pricingPlan?.name}</span>
            </TableCell>
            <TableCell align="center">
              <div onClick={() => onViewUpdate(update._id)} data-testid={'test-' + index}>
                <i className="fa-regular fa-magnifying-glass table-action-icon"></i>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export const PricingBotActivityPresenter = ({
  updates,
  onViewUpdate,
  selectedPlanId,
  setSelectedPlanId,
  activePlan,
  isTestRunning,
  setIsTestRunning,
  isManualRunning,
  setIsManualRunning,
}: PricingBotActivityPresenterProps): JSX.Element => {
  const { user } = useUser()

  selectedPlanId.length
  setSelectedPlanId.length
  activePlan?.disallowNesting

  if (!user) return <></>

  const [activeTab, setActiveTab] = useState<PriceUpdateTab>(PriceUpdateTab.Nightly)

  const nightlyRuns = updates.filter(
    (update) => update.details?.type === PricingJobType.Nightly && update.status === 'published'
  )
  const testRuns = updates.filter(
    (update) => update.details?.type === PricingJobType.TestRun && update.status === 'done'
  )
  const manualRuns = updates.filter(
    (update) => update.details?.type === PricingJobType.ManualRun && update.status === 'published'
  )

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const numOfTestUpdates = testRuns.length
  useEffect(() => {
    if (isTestRunning) {
      setIsTestRunning('')
      setActiveTab(PriceUpdateTab.Test)
    }
  }, [numOfTestUpdates])

  const numOfManualUpdates = manualRuns.length
  useEffect(() => {
    if (isManualRunning) {
      setIsManualRunning('')
      setActiveTab(PriceUpdateTab.Manual)
    }
  }, [numOfManualUpdates])

  // A failed run never reaches done/published: without this the run buttons stay disabled
  // until the page is reloaded
  const numOfFailedTestRuns = updates.filter(
    (update) => update.details?.type === PricingJobType.TestRun && update.status === 'failed'
  ).length
  useEffect(() => {
    if (isTestRunning) {
      setIsTestRunning('')
      enqueueSnackbar(t('priceUpdates.runFailed'), { variant: 'error' })
    }
  }, [numOfFailedTestRuns])

  const numOfFailedManualRuns = updates.filter(
    (update) => update.details?.type === PricingJobType.ManualRun && update.status === 'failed'
  ).length
  useEffect(() => {
    if (isManualRunning) {
      setIsManualRunning('')
      enqueueSnackbar(t('priceUpdates.runFailed'), { variant: 'error' })
    }
  }, [numOfFailedManualRuns])

  return (
    <Box>
      <h6>{t('priceUpdates.runHistory')}</h6>
      <Box className="run-history-region">
        <Tabs
          value={activeTab}
          onChange={(_e, newVal) => {
            setActiveTab(newVal)
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`${t('priceUpdates.recentCompletedRuns')}`} />
          <Tab label={`${t('priceUpdates.recentManualRuns')}`} hidden />
          <Tab label={`${t('priceUpdates.recentTestRuns')}`} data-testid="recent-test-run" />
        </Tabs>
        <Box padding={2}>
          {activeTab === PriceUpdateTab.Nightly &&
            (nightlyRuns.length ? (
              <PriceUpdatesTable onViewUpdate={onViewUpdate} updates={nightlyRuns} />
            ) : (
              <Typography variant="caption" color="textSecondary">
                {t('priceUpdates.noRecentCompletedRuns')}
              </Typography>
            ))}

          {activeTab === PriceUpdateTab.Manual &&
            (manualRuns.length ? (
              <PriceUpdatesTable onViewUpdate={onViewUpdate} updates={manualRuns} />
            ) : (
              <Typography variant="caption" color="textSecondary">
                {t('priceUpdates.noRecentManualRuns')}
              </Typography>
            ))}

          {activeTab === PriceUpdateTab.Test &&
            (testRuns.length ? (
              <PriceUpdatesTable onViewUpdate={onViewUpdate} updates={testRuns} />
            ) : (
              <Typography variant="caption" color="textSecondary">
                {t('priceUpdates.noRecentTestRuns')}
              </Typography>
            ))}
        </Box>
      </Box>
    </Box>
  )
}
